import { useState, useEffect } from 'react';
import { getDateNumericValue } from '../utils/getDateNumericValue';

// Tarih formatlama yardımcı fonksiyonu
const formatDate = (date) => {
    if (!date) return 'Tarih belirtilmemiş';
    
    try {
        // CustomDate objesi için toString metodu varsa
        if (typeof date.toString === 'function' && date.toString() !== '[object Object]') {
            return date.toString();
        }
        
        // Direct access to day, month, year properties (CustomDate style)
        if (date.day !== undefined && date.month !== undefined && date.year !== undefined) {
            const monthNames = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
            return `${date.day} ${monthNames[date.month]} ${date.year}`;
        }
        
        // Check for CustomDate methods (getFullYear, getMonth, getDate)
        if (typeof date.getFullYear === 'function' && 
            typeof date.getMonth === 'function' && 
            typeof date.getDate === 'function') {
            
            const day = date.getDate();
            const month = date.getMonth();
            const year = date.getFullYear();
            const monthNames = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
            
            // BC tarihleri için
            if (year < 0 || (date.isBC && date.isBC === true)) {
                return `${day} ${monthNames[month]} MÖ ${Math.abs(year)}`;
            } else {
                return `${day} ${monthNames[month]} ${year}`;
            }
        }
        
        // JavaScript Date objesi için
        if (date instanceof Date) {
            const day = date.getDate();
            const month = date.getMonth();
            const year = date.getFullYear();
            const monthNames = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
            
            // BC tarihleri için
            if (year < 0) {
                return `${day} ${monthNames[month]} MÖ ${Math.abs(year)}`;
            } else {
                return `${day} ${monthNames[month]} ${year}`;
            }
        }
        
        // Diğer tüm durumlar için toString kullan
        return String(date);
    } catch (error) {
        console.error('Tarih formatlama hatası:', error);
        return 'Geçersiz Tarih';
    }
};

const SearchPopup = ({ 
    isOpen, 
    onClose, 
    onEventSelect, 
    categories = [], 
    filteredCategories = [], 
    setFilteredCategories,
    timeFilter = { active: false },
    textFilter = { active: false }
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [textFilterActive, setTextFilterActive] = useState(textFilter?.active || false);
    const [searchInTitle, setSearchInTitle] = useState(true);
    const [searchInDescription, setSearchInDescription] = useState(true);
    
    // Zaman aralığı filtreleri
    const [useTimeFilter, setUseTimeFilter] = useState(false);
    const [startYear, setStartYear] = useState('');
    const [endYear, setEndYear] = useState('');
    const [dateFilterError, setDateFilterError] = useState('');
    const [timeFilterActive, setTimeFilterActive] = useState(timeFilter?.active || false);
    
    // Kategori filtreleri
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [useCategoryFilter, setUseCategoryFilter] = useState(false);

    // Initialize time filter from props
    useEffect(() => {
        if (timeFilter) {
            setTimeFilterActive(timeFilter.active || false);
            if (timeFilter.startYear !== null && timeFilter.startYear !== undefined) {
                setStartYear(String(timeFilter.startYear));
            }
            if (timeFilter.endYear !== null && timeFilter.endYear !== undefined) {
                setEndYear(String(timeFilter.endYear));
            }
        }
    }, [timeFilter]);

    // Initialize text filter from props
    useEffect(() => {
        if (textFilter) {
            setTextFilterActive(textFilter.active || false);
            if (textFilter.text) {
                setSearchTerm(textFilter.text);
            }
            if (textFilter.searchInTitle !== undefined) {
                setSearchInTitle(textFilter.searchInTitle);
            }
            if (textFilter.searchInDescription !== undefined) {
                setSearchInDescription(textFilter.searchInDescription);
            }
        }
    }, [textFilter]);

    // Add a helper function to extract category ID properly
    const extractCategoryId = (categoryIdInput) => {
        if (!categoryIdInput) return null;
        
        try {
            // If it's already a string
            if (typeof categoryIdInput === 'string') {
                return categoryIdInput;
            }
            
            // If it's an object with _id property (MongoDB style)
            if (typeof categoryIdInput === 'object' && categoryIdInput._id) {
                return String(categoryIdInput._id);
            }
            
            // If it's an object with id property
            if (typeof categoryIdInput === 'object' && categoryIdInput.id) {
                return String(categoryIdInput.id);
            }
            
            // Try to stringify the object - this works for MongoDB ObjectId
            if (typeof categoryIdInput === 'object') {
                // Check for toString method
                if (typeof categoryIdInput.toString === 'function') {
                    const idStr = categoryIdInput.toString();
                    // Only return if it looks like a valid ID (avoid [object Object])
                    if (idStr !== '[object Object]') {
                        return idStr;
                    }
                }
                
                // Last resort - get any property that looks like an ID
                for (const key in categoryIdInput) {
                    if (['_id', 'id', '$oid'].includes(key) && categoryIdInput[key]) {
                        return String(categoryIdInput[key]);
                    }
                }
            }
            
            // If all else fails
            console.warn('Could not extract category ID from:', categoryIdInput);
            return null;
        } catch (error) {
            console.error('Error extracting category ID:', error);
            return null;
        }
    };
    
    // Helper function to check if two category IDs match
    const categoriesMatch = (id1, id2) => {
        const id1Str = String(id1);
        const id2Str = String(id2);
        
        // Exact match
        if (id1Str === id2Str) return true;
        
        // Substring match for longer IDs
        if (id1Str.length > 10 && id2Str.length > 10) {
            return id1Str.endsWith(id2Str.substring(id2Str.length - 10)) ||
                   id2Str.endsWith(id1Str.substring(id1Str.length - 10));
        }
        
        return false;
    };
    
    // Update the getCategoryNameById function to use our new helper
    const getCategoryNameById = (categoryId) => {
        if (!categoryId) return 'Kategorisiz';
        
        try {
            const extractedId = extractCategoryId(categoryId);
            if (!extractedId) return 'Kategorisiz';
            
            // Try an exact match first
            let category = categories.find(cat => String(cat._id) === extractedId);
            
            // If not found, try additional matching strategies
            if (!category && extractedId.length > 10) {
                category = categories.find(cat => 
                    String(cat._id).endsWith(extractedId.substring(extractedId.length - 10))
                );
            }
            
            // Return category name or default if not found
            return category ? category.name : 'Kategorisiz';
        } catch (error) {
            console.error('Error finding category name:', error);
            return 'Kategorisiz';
        }
    };

    // Update the getCategoryColorById function to use our new helper
    const getCategoryColorById = (categoryId) => {
        if (!categoryId) return '#808080'; // Default gray
        
        try {
            const extractedId = extractCategoryId(categoryId);
            if (!extractedId) return '#808080';
            
            // Try an exact match first
            let category = categories.find(cat => String(cat._id) === extractedId);
            
            // If not found, try additional matching strategies
            if (!category && extractedId.length > 10) {
                category = categories.find(cat => 
                    String(cat._id).endsWith(extractedId.substring(extractedId.length - 10))
                );
            }
            
            // Return category color or default if not found
            return category ? category.color : '#808080';
        } catch (error) {
            console.error('Error finding category color:', error);
            return '#808080';
        }
    };

    // Filtered categorileri güncelle
    useEffect(() => {
        if (setFilteredCategories) {
            if (useCategoryFilter && selectedCategories.length > 0) {
                // Çizelgede gösterilecek kategori filtresini etkinleştir
                setFilteredCategories(selectedCategories);
                // Notify the timeline that filters have been updated
                document.dispatchEvent(new CustomEvent('timelineFilterUpdated'));
            } else {
                // Filtreyi kaldır - tüm kategorileri göster
                setFilteredCategories([]);
                // Notify the timeline that filters have been updated
                document.dispatchEvent(new CustomEvent('timelineFilterUpdated'));
            }
        }
    }, [selectedCategories, useCategoryFilter, setFilteredCategories]);

    // Send time filter to parent component when active
    useEffect(() => {
        if (window.setTimelineTimeFilter) {
            if (timeFilterActive && (startYear !== '' || endYear !== '')) {
                const timeFilter = {
                    active: true,
                    startYear: startYear === '' ? null : Number(startYear),
                    endYear: endYear === '' ? null : Number(endYear)
                };
                window.setTimelineTimeFilter(timeFilter);
                // Notify the timeline that filters have been updated
                document.dispatchEvent(new CustomEvent('timelineFilterUpdated'));
            } else {
                window.setTimelineTimeFilter({ active: false });
                // Notify the timeline that filters have been updated
                document.dispatchEvent(new CustomEvent('timelineFilterUpdated'));
            }
        }
    }, [startYear, endYear, timeFilterActive]);

    // Send text filter to parent component when active
    useEffect(() => {
        if (window.setTimelineTextFilter) {
            if (textFilterActive && searchTerm.trim() !== '') {
                window.setTimelineTextFilter({
                    active: true,
                    text: searchTerm.trim(),
                    searchInTitle,
                    searchInDescription
                });
                // Notify the timeline that filters have been updated
                document.dispatchEvent(new CustomEvent('timelineFilterUpdated'));
            } else {
                window.setTimelineTextFilter({ active: false });
                // Notify the timeline that filters have been updated
                document.dispatchEvent(new CustomEvent('timelineFilterUpdated'));
            }
        }
    }, [searchTerm, textFilterActive, searchInTitle, searchInDescription]);

    // Dış filtrelenmiş kategorileri takip et
    useEffect(() => {
        if (filteredCategories.length > 0) {
            setSelectedCategories(filteredCategories);
            setUseCategoryFilter(true);
        }
    }, [filteredCategories]);

    // Kategorileri seçme/kaldırma işlevi
    const handleCategoryToggle = (categoryId) => {
        setSelectedCategories(prev => {
            // Check if category is already selected
            const isSelected = prev.some(id => categoriesMatch(id, categoryId));
            
            if (isSelected) {
                // Remove category
                return prev.filter(id => !categoriesMatch(id, categoryId));
            } else {
                // Add category
                return [...prev, categoryId];
            }
        });
    };

    // Tüm kategorileri seçme/kaldırma işlevi
    const handleToggleAllCategories = () => {
        if (categories.length === 0) return;
        
        // Check if all categories are already selected
        const allSelected = categories.every(cat => 
            selectedCategories.some(selectedId => categoriesMatch(selectedId, cat._id))
        );
        
        if (allSelected) {
            // Clear all selections
            setSelectedCategories([]);
        } else {
            // Select all categories
            setSelectedCategories(categories.map(cat => cat._id));
        }
    };

    // Helper function to check if a category ID is in the selected list
    const isCategorySelected = (categoryId) => {
        if (!categoryId || !selectedCategories.length) return false;
        
        try {
            const extractedId = extractCategoryId(categoryId);
            if (!extractedId) return false;
            
            return selectedCategories.some(selectedId => {
                const selectedIdStr = String(selectedId);
                
                // Try exact match first
                if (selectedIdStr === extractedId) return true;
                
                // If ID is long, try substring match
                if (extractedId.length > 10 && selectedIdStr.length > 10) {
                    return extractedId.endsWith(selectedIdStr.substring(selectedIdStr.length - 10)) ||
                           selectedIdStr.endsWith(extractedId.substring(extractedId.length - 10));
                }
                
                return false;
            });
        } catch (error) {
            console.error('Error matching category:', error);
            return false;
        }
    };

    const performSearch = () => {
        // Determine if time filter should be applied based on whether date fields contain values
        const shouldUseTimeFilter = startYear !== '' || endYear !== '';
        
        // Tarih filtresi kontrolü
        if (shouldUseTimeFilter) {
            if (startYear !== '' && endYear !== '' && Number(startYear) > Number(endYear)) {
                setDateFilterError('Başlangıç tarihi, bitiş tarihinden sonra olamaz');
                return;
            } else {
                setDateFilterError('');
            }
        }

        // Global events listesinden arama yap
        const events = window.timelineEvents || [];
        const results = events.filter(event => {
            // Metin araması kontrolü
            let textMatch = true;
            if (searchTerm.trim() !== '') {
                // Aranacak alanları al ve null/undefined kontrolü yap
                const title = event.title?.toLowerCase() || '';
                const description = event.description?.toLowerCase() || '';
                const term = searchTerm.toLowerCase();
                
                // Check if we should search in title or description or both
                const titleMatch = searchInTitle && (title === term || title.includes(term) || title.startsWith(term));
                const descriptionMatch = searchInDescription && (description === term || description.includes(term) || description.startsWith(term));
                
                // Match if either title or description matches (or both)
                textMatch = titleMatch || descriptionMatch;
            }
            
            // Tarih aralığı kontrolü
            let dateMatch = true;
            if (shouldUseTimeFilter) {
                const eventDate = event.startDate;
                if (!eventDate) return false;
                
                // CustomDate nesnesinden yıl değerini doğru şekilde al
                let eventYear = eventDate.getFullYear();

                // Başlangıç yılı kontrolü
                if (startYear !== '') {
                    const startYearNum = Number(startYear);
                    if (eventYear < startYearNum) dateMatch = false;
                }
                
                // Bitiş yılı kontrolü
                if (endYear !== '') {
                    const endYearNum = Number(endYear);
                    if (eventYear > endYearNum) dateMatch = false;
                }
            }
            
            // Kategori kontrolü - improved version
            let categoryMatch = true;
            if (selectedCategories.length > 0) {
                categoryMatch = isCategorySelected(event.category_id);
            }
            
            // Tüm kriterlere uygunsa sonuçlara ekle
            return textMatch && dateMatch && categoryMatch;
        });

        // Tarihe göre sıralama
        results.sort((a, b) => {
            const dateA = getDateNumericValue(a.startDate);
            const dateB = getDateNumericValue(b.startDate);
            return dateA - dateB;
        });

        setSearchResults(results);
    };

    // Update the useEffect dependency array - remove useTimeFilter since we're now calculating it dynamically
    useEffect(() => {
        performSearch();
    }, [searchTerm, startYear, endYear, selectedCategories, useCategoryFilter]);

    // Sonuçları tarih gruplarına ayırma fonksiyonu
    const groupResultsByDate = (results) => {
        // Gruplama için boş nesne
        const groups = {};
        
        // Her sonucu kontrol et
        results.forEach(event => {
            if (!event.startDate) return;
            
            // Event tarihinin yılı - CustomDate için doğru kontrolü yap
            let year = event.startDate.getFullYear();
            
            // Grup adı oluştur
            const groupName = `${year < 0 ? 'MÖ ' + Math.abs(year) : year}`;
            
            // Grubu oluştur veya olaya ekle
            if (!groups[groupName]) {
                groups[groupName] = [];
            }
            groups[groupName].push(event);
        });
        
        // Sonuçları sıralı gruplar halinde döndür
        return Object.entries(groups)
            .sort((a, b) => {
                // Grup adlarını sayısal değerlere çevir ve sırala
                const yearA = a[0].startsWith('MÖ ') ? -parseInt(a[0].replace('MÖ ', '')) : parseInt(a[0]);
                const yearB = b[0].startsWith('MÖ ') ? -parseInt(b[0].replace('MÖ ', '')) : parseInt(b[0]);
                return yearA - yearB;
            })
            .map(([year, events]) => {
                // Olayları priority değerlerine göre sırala (yüksek öncelikli olanlar üstte)
                const sortedEvents = [...events].sort((a, b) => {
                    // Priority değerlerini al veya varsayılan değer kullan
                    const priorityA = a.priority || 1;
                    const priorityB = b.priority || 1;
                    // Yüksek priorityli olanlar önce gelsin (azalan sıralama)
                    return priorityB - priorityA;
                });
                
                return { year, events: sortedEvents };
            });
    };

    // Sonuçları gruplandır
    const groupedResults = groupResultsByDate(searchResults);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 z-[80] flex items-start justify-center">
            <div 
                className="bg-gray-900 rounded-xl shadow-2xl p-6 w-[700px] max-h-[85vh] overflow-y-auto mt-20"
                onClick={e => e.stopPropagation()}
            >
                {/* Arama Başlığı */}
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-semibold text-white">Zaman Çizelgesi Filtrele</h2>
                    <button 
                        onClick={onClose}
                        className="text-gray-400 hover:text-white transition-colors"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Arama Kutusu - Now with active toggle and search area options */}
                <div className="bg-gray-800 p-4 rounded-lg mb-6">
                    <div className="flex justify-between items-center mb-3">
                        <h3 className="text-white font-medium">Metin Araması</h3>
                        <label className="inline-flex items-center">
                            <input 
                                type="checkbox" 
                                checked={textFilterActive} 
                                onChange={() => setTextFilterActive(!textFilterActive)}
                                className="form-checkbox rounded text-blue-500 h-4 w-4"
                            />
                            <span className="ml-2 text-sm text-gray-300">Aktif</span>
                        </label>
                    </div>
                    <div className="relative">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Olay adı veya açıklaması ara..."
                            className="w-full p-4 pl-12 bg-gray-700 text-white border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            className="h-6 w-6 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>

                    {/* Search Area Options */}
                    <div className="mt-3 flex flex-wrap gap-4">
                        <label className="inline-flex items-center cursor-pointer">
                            <input 
                                type="checkbox" 
                                checked={searchInTitle} 
                                onChange={() => setSearchInTitle(!searchInTitle)}
                                className="form-checkbox rounded text-blue-500 h-4 w-4"
                            />
                            <span className="ml-2 text-sm text-gray-300">Başlık</span>
                        </label>
                        <label className="inline-flex items-center cursor-pointer">
                            <input 
                                type="checkbox" 
                                checked={searchInDescription} 
                                onChange={() => setSearchInDescription(!searchInDescription)}
                                className="form-checkbox rounded text-blue-500 h-4 w-4"
                            />
                            <span className="ml-2 text-sm text-gray-300">Açıklama</span>
                        </label>

                        {/* Warning if neither is selected */}
                        {!searchInTitle && !searchInDescription && searchTerm.trim() !== '' && (
                            <div className="w-full mt-2">
                                <p className="text-yellow-500 text-xs">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                    Lütfen en az bir arama alanı seçin
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="mt-3 text-xs text-gray-400">
                        {textFilterActive && searchTerm.trim() !== '' 
                            ? <span className="bg-blue-500 text-white px-2 py-0.5 rounded-full text-xs">
                                Zaman çizelgesinde filtreleniyor
                                {searchInTitle && !searchInDescription && " (Sadece başlıklarda)"}
                                {!searchInTitle && searchInDescription && " (Sadece açıklamalarda)"}
                              </span>
                            : <span>Sonuçlarda filtreleniyor</span>
                        }
                    </div>
                </div>

                {/* Filter Summary - New section showing active filters */}
                {(textFilterActive || timeFilterActive || useCategoryFilter) && (
                    <div className="mb-4 bg-gray-800/50 p-3 rounded-lg border border-gray-700">
                        <div className="flex flex-wrap gap-2">
                            <div className="text-sm text-gray-400">Aktif Filtreler:</div>
                            {textFilterActive && searchTerm.trim() !== '' && (
                                <span className="bg-purple-800/70 text-white text-xs px-2 py-1 rounded flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    Metin: "{searchTerm.trim()}"
                                    {searchInTitle && !searchInDescription && " (Başlık)"}
                                    {!searchInTitle && searchInDescription && " (Açıklama)"}
                                </span>
                            )}
                            {timeFilterActive && (startYear !== '' || endYear !== '') && (
                                <span className="bg-green-800/70 text-white text-xs px-2 py-1 rounded flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    Tarih: {startYear && endYear ? `${startYear} - ${endYear}` : 
                                           startYear ? `${startYear}'den sonra` : 
                                           endYear ? `${endYear}'den önce` : ''}
                                </span>
                            )}
                            {useCategoryFilter && selectedCategories.length > 0 && (
                                <span className="bg-blue-800/70 text-white text-xs px-2 py-1 rounded flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                    </svg>
                                    Kategori: {selectedCategories.length} seçili
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* Filtre Bölümleri - Ana Kapsayıcı */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {/* Tarih Filtresi - Sol Taraf */}
                    <div className="bg-gray-800 p-4 rounded-lg">
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="text-white font-medium">Zaman Aralığı</h3>
                            <label className="inline-flex items-center">
                                <input 
                                    type="checkbox" 
                                    checked={timeFilterActive} 
                                    onChange={() => setTimeFilterActive(!timeFilterActive)}
                                    className="form-checkbox rounded text-blue-500 h-4 w-4"
                                />
                                <span className="ml-2 text-sm text-gray-300">Aktif</span>
                            </label>
                        </div>
                        
                        <div className="space-y-3">
                            <div>
                                <label className="block text-gray-400 text-sm mb-1">Başlangıç Yılı</label>
                                <input 
                                    type="number" 
                                    value={startYear}
                                    onChange={(e) => setStartYear(e.target.value)}
                                    placeholder="Örn. -500 (MÖ) veya 1923"
                                    className="w-full p-2 bg-gray-700 text-white border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-gray-400 text-sm mb-1">Bitiş Yılı</label>
                                <input 
                                    type="number" 
                                    value={endYear}
                                    onChange={(e) => setEndYear(e.target.value)}
                                    placeholder="Örn. 2025"
                                    className="w-full p-2 bg-gray-700 text-white border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                        
                        {dateFilterError && (
                            <p className="text-red-500 text-sm mt-2">{dateFilterError}</p>
                        )}
                        
                        <div className="mt-3 text-xs text-gray-400">
                            {timeFilterActive && (startYear !== '' || endYear !== '') 
                                ? <span className="bg-blue-500 text-white px-2 py-0.5 rounded-full text-xs">
                                    Zaman çizelgesinde filtreleniyor
                                  </span>
                                : <span>Sonuçlarda filtreleniyor</span>
                            }
                        </div>
                    </div>
                    
                    {/* Kategori Filtresi - Sağ Taraf */}
                    <div className="bg-gray-800 p-4 rounded-lg">
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="text-white font-medium">Kategoriler</h3>
                            <label className="inline-flex items-center">
                                <input 
                                    type="checkbox" 
                                    checked={useCategoryFilter} 
                                    onChange={() => setUseCategoryFilter(!useCategoryFilter)}
                                    className="form-checkbox rounded text-blue-500 h-4 w-4"
                                />
                                <span className="ml-2 text-sm text-gray-300">Aktif</span>
                            </label>
                        </div>
                        
                        <div className="space-y-2 max-h-[150px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-900">
                            {categories.length > 0 ? (
                                <>
                                    <div className="flex justify-between items-center pb-2 border-b border-gray-700">
                                        <button 
                                            onClick={handleToggleAllCategories}
                                            className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                                        >
                                            {selectedCategories.length === categories.length ? 'Tümünü Kaldır' : 'Tümünü Seç'}
                                        </button>
                                        
                                        <div className="text-xs text-gray-400">
                                            {useCategoryFilter && selectedCategories.length > 0 
                                                ? <span className="bg-blue-500 text-white px-2 py-0.5 rounded-full text-xs">
                                                    Zaman çizelgesinde filtreleniyor
                                                  </span>
                                                : <span>Sonuçlarda filtreleniyor</span>
                                            }
                                        </div>
                                    </div>
                                    
                                    {categories.map(category => (
                                        <div key={category._id} className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id={`category-${category._id}`}
                                                checked={selectedCategories.includes(category._id)}
                                                onChange={() => handleCategoryToggle(category._id)}
                                                className="form-checkbox rounded mr-2"
                                            />
                                            <label 
                                                htmlFor={`category-${category._id}`} 
                                                className="flex items-center cursor-pointer text-gray-300 hover:text-white transition-colors"
                                            >
                                                <span 
                                                    className="inline-block w-3 h-3 rounded-full mr-2" 
                                                    style={{ backgroundColor: category.color || '#808080' }}
                                                ></span>
                                                {category.name || 'İsimsiz Kategori'}
                                            </label>
                                        </div>
                                    ))}
                                </>
                            ) : (
                                <p className="text-gray-400 text-sm italic">Henüz kategori eklenmemiş</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Sonuçlar Başlığı */}
                <div className="flex justify-between items-center mb-4 bg-gray-800 p-3 rounded-lg">
                    <h3 className="text-lg font-medium text-white">Sonuçlar</h3>
                    <span className="text-gray-300 text-sm px-3 py-1 bg-gray-700 rounded-full">{searchResults.length} sonuç bulundu</span>
                </div>

                {/* Sonuçlar Listesi - Gruplandırılmış */}
                <div className="space-y-6">
                    {searchResults.length === 0 && (
                        <div className="bg-gray-800 rounded-lg p-8 text-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-gray-400 text-lg">Filtrelere uygun sonuç bulunamadı</p>
                            <p className="text-gray-500 text-sm mt-2">Lütfen arama kriterlerinizi değiştirin</p>
                        </div>
                    )}
                    
                    {/* Tarih gruplarına göre olayları listele */}
                    {groupedResults.map((group, groupIndex) => (
                        <div key={groupIndex} className="space-y-3">
                            {/* Tarih Başlığı */}
                            <h4 className="text-md font-semibold text-blue-400 border-b border-blue-800 pb-1 mb-2">
                                {group.year} {group.events.length > 1 ? `(${group.events.length} olay)` : ''}
                            </h4>
                            
                            {/* Bu tarihteki olaylar */}
                            <div className="space-y-3">
                                {group.events.map((event, eventIndex) => (
                                    <div
                                        key={eventIndex}
                                        onClick={() => {
                                            onEventSelect(event);
                                            onClose();
                                        }}
                                        className="bg-gray-800 p-4 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors flex flex-col"
                                    >
                                        <div className="flex justify-between">
                                            <h3 className="text-lg font-medium text-white">{event.title}</h3>
                                        </div>
                                        
                                        <p className="text-gray-400 text-sm line-clamp-2 mt-1">{event.description}</p>
                                        
                                        <div className="flex justify-between items-center mt-3 pt-2 border-t border-gray-700">
                                            <div className="text-sm text-blue-300 font-medium">
                                                {formatDate(event.startDate)}
                                                {event.endDate && ` - ${formatDate(event.endDate)}`}
                                            </div>
                                            
                                            {/* Category display */}
                                            <div className="flex items-center">
                                                <span 
                                                    className="inline-block w-2 h-2 rounded-full mr-1" 
                                                    style={{ 
                                                        backgroundColor: event.category_id 
                                                            ? getCategoryColorById(event.category_id) 
                                                            : '#808080'
                                                    }}
                                                ></span>
                                                <span className="text-xs text-gray-400">
                                                    {event.category_id 
                                                        ? getCategoryNameById(event.category_id) 
                                                        : 'Kategorisiz'
                                                    }
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SearchPopup; 